<template>
  <div v-if="submitted && submittedHtml" v-html="submittedHtml"></div>
  <div
    v-if="!submitted"
    class="relative flex flex-col overflow-hidden rounded-md border-t-[6px] border-t-dark bg-white shadow-3 lg:flex-row"
  >
    <site-loader v-if="submitting" :background="true" />
    <div class="relative flex w-full flex-col p-5">
      <div class="mb-5 flex items-center justify-between">
        <h3 class="mb-0 text-xl font-semibold sm:text-3xl">
          <span v-if="step == 1">{{ t("Select insurance options") }}</span>
          <span v-if="step == 2">{{ t("Enter details") }}</span>
        </h3>
        <div class="flex items-center text-sm">
          <a v-if="step > 1" href="#" class="mr-1.5 inline-flex text-red-500" @click.prevent="step = step - 1">
            <span class="icon slider-arrow-next size-5 rotate-180"></span>
          </a>
          <span class="hidden text-sm sm:inline">{{ t("Step {step} of 2", { step: step }) }}</span>
        </div>
      </div>
      <ul v-if="persons.length > 1" class="persons-menu mb-5 flex border-b border-dark">
        <li
          v-for="(person, index) in persons"
          :key="index"
          :class="
            'cursor-pointer px-3.5 py-1.5 text-sm font-medium text-dark' +
            (selectedPerson == person.uniqueId ? ' bg-dark text-white' : '')
          "
          @click="selectedPerson = person.uniqueId"
        >
          <span class="title">{{ getName(person, index) }}</span>
          <span v-if="index > 0" class="icon close-icon ms-2.5 size-2.5" @click="removePerson(person.uniqueId)"></span>
        </li>
      </ul>
      <calculator-person-lamal
        v-for="person in persons"
        v-show="selectedPerson == person.uniqueId"
        :key="person.uniqueId"
        :step="step"
        :person="person"
        :initial-postcode="initialPostcode"
        :accident-cover-tooltip="accidentCoverTooltip"
        :doctor-tooltip="doctorTooltip"
        :select-doctor-text="selectDoctorText"
        :errors="errors[person.uniqueId] || []"
        :insurance-providers="insuranceProviders"
        @update="updatePerson($event)"
      />
      <div class="flex justify-end">
        <a v-if="step == 1" href="#" class="text-sm text-red-500" @click.prevent="addPerson">{{ t("Add a person") }}</a>
      </div>
    </div>
    <div class="flex w-full shrink-0 flex-col justify-between bg-dark p-5 py-7 text-white lg:w-[30%]">
      <div>
        <p class="mb-2.5 text-xl font-semibold">{{ t("Exact Calculation") }}</p>
        <p class="text-xs text-dark-1">
          {{
            t("Price per month in {currency}", {
              currency: currency(),
            })
          }}
        </p>
        <div class="flex flex-col text-sm font-medium text-dark-10">
          <div v-for="(person, index) in persons" :key="index">
            <div v-if="person.price" class="flex justify-between">
              <span v-if="persons.length > 1">{{
                t("Person {number}: {plan}", {
                  number: index + 1,
                  plan: person.price.plan.title,
                })
              }}</span>
              <span v-if="persons.length == 1">{{ person.price.plan.title }}</span>
              <span class="shrink-0">
                {{ formatCurrency(person.price.value) }}
              </span>
            </div>
            <div v-if="!person.price" class="text-red-500">
              <span v-if="persons.length > 1">{{
                t("Person {number}: please choose a plan", {
                  number: index + 1,
                })
              }}</span>
              <span v-if="persons.length == 1">{{ t("Please choose a plan") }}</span>
            </div>
            <div v-if="person.price?.accident && person.accidentCover" class="flex justify-between">
              <span v-if="persons.length > 1">{{ t("Person {number}: Accident Cover", { number: index + 1 }) }}</span>
              <span v-if="persons.length == 1">{{ t("Accident Cover") }}</span>
              <span class="shrink-0">
                {{ formatCurrency(person.price.accident.price) }}
              </span>
            </div>
            <div v-if="person.optionals">
              <div v-for="optional in person.optionals" :key="optional.id" class="flex justify-between">
                <span v-if="persons.length > 1">{{
                  t("Person {number}: {plan}", {
                    number: index + 1,
                    plan: optional.title,
                  })
                }}</span>
                <span v-if="persons.length == 1">{{ optional.title }}</span>
                <span class="shrink-0">
                  {{ formatCurrency(optional.price) }}
                </span>
              </div>
            </div>
          </div>
        </div>
        <div class="mt-2.5 flex justify-between border-t border-dark-5 py-2.5">
          <span class="text-sm font-semibold">{{ t("Total") }}</span>
          <span class="text-right">{{ formatCurrency(total) }}</span>
        </div>
        <div class="flex rounded-md border border-white p-2.5">
          <span class="icon red-info mr-2 size-4.5"></span>
          <div class="rich-text small-p" v-html="deductibleInfo"></div>
        </div>
      </div>
      <div class="mt-5 flex w-full flex-col">
        <button v-if="step == 1" type="button" class="btn btn-red" @click="toStep2()">
          {{ t("Continue").toUpperCase() }}
        </button>
        <div v-show="step == 2" class="mb-2 flex items-center text-dark-1">
          <input
            :id="uniqueId('calculator-conditions')"
            v-model="conditionsAccepted"
            type="checkbox"
            class="conditions-input mr-2.5"
          />
          <label
            :for="uniqueId('calculator-conditions')"
            :class="'rich-text' + (errorConditions ? ' text-red-500' : '')"
            v-html="termsConditions"
          ></label>
        </div>
        <p v-for="(error, index) in freeformErrors" :key="index" class="mb-2.5 text-sm text-red-500">
          {{ error }}
        </p>
        <button v-if="step == 2" type="button" class="btn btn-red" :disabled="submitting" @click="submit()">
          {{ t("Receive").toUpperCase() }}
        </button>
      </div>
    </div>
  </div>
</template>

<script>
/* globals App Craft $ */
import CalculatorPersonLamal from "./CalculatorPersonLamal.vue";
import validateEmail from "filter-validate-email";
import SiteLoader from "./SiteLoader.vue";

export default {
  components: {
    CalculatorPersonLamal,
    SiteLoader,
  },
  props: {
    deductibleInfo: {
      type: String,
      required: true,
    },
    accidentCoverTooltip: {
      type: String,
    },
    doctorTooltip: {
      type: String,
    },
    selectDoctorText: {
      type: String,
    },
    termsConditions: {
      type: String,
      required: true,
    },
    initialDob: {
      type: String,
    },
    initialGender: {
      type: String,
    },
    initialDeductible: {
      type: Object,
    },
    initialPostcode: {
      type: Object,
    },
    freeformSubmit: {
      type: Function,
      required: true,
    },
    freeformErrors: {
      type: Array,
    },
    submitted: {
      type: Boolean,
      required: true,
    },
    submitting: {
      type: Boolean,
      required: true,
    },
    submittedHtml: {
      type: String,
    },
  },
  data() {
    return {
      persons: [],
      insuranceProviders: [],
      errors: {},
      selectedPerson: 0,
      conditionsAccepted: false,
      errorConditions: false,
      step: 1,
      id: Math.floor(Math.random() * 10000),
    };
  },
  computed: {
    total() {
      let total = 0;
      for (let person of this.persons) {
        if (person.price) {
          total += person.price.value;
          if (person.accidentCover && person.price.accident) {
            total += person.price.accident.price;
          }
        }
        if (person.optionals) {
          for (let optional of person.optionals) {
            total += optional.price;
          }
        }
      }
      return total;
    },
  },
  created() {
    this.addPerson();
    this.fetchInsuranceProviders();
  },
  methods: {
    uniqueId(string) {
      return string + "-" + this.id;
    },
    submit() {
      this.validateStep2();
      if (Object.keys(this.errors).length == 0 && this.conditionsAccepted) {
        this.freeformSubmit({
          persons: this.persons.map((p) => {
            return [
              p.firstName + " " + p.lastName,
              p.gender,
              p.email,
              p.dob,
              p.price.plan.title,
              p.postcode.label,
              p.deductible.label,
              p.accidentCover ? "1" : "0",
              p.optionals.map((o) => o.title).join(", "),
              p.insuranceProvider?.label,
              p.provider?.name,
            ];
          }),
          total: this.total,
        });
      }
    },
    getName(person, index) {
      if (person.firstName && person.lastName) {
        return `${person.firstName} ${person.lastName}`;
      }
      return this.t("Person {number}", { number: index + 1 });
    },
    addPerson() {
      let uniqueId = Math.floor(Math.random() * 10000);
      this.persons.push({
        uniqueId: uniqueId,
        dob: this.initialDob,
        deductible: this.initialDeductible,
        postcode: this.initialPostcode,
        gender: this.initialGender,
        accidentCover: true,
        firstName: "test",
        lastName: "test",
        email: "test@test.com",
      });
      this.selectedPerson = uniqueId;
    },
    removePerson(uniqueId) {
      this.selectedPerson = this.persons[0].uniqueId;
      this.persons = this.persons.filter((a) => a.uniqueId != uniqueId);
    },
    updatePerson(event) {
      this.persons = this.persons.map((a) => {
        if (a.uniqueId == this.selectedPerson) {
          return { ...a, ...event };
        }
        return a;
      });
    },
    toStep2() {
      this.validateStep1();
      if (Object.keys(this.errors).length == 0) {
        this.step = 2;
        this.setCookie();
      }
    },
    setCookie() {
      App.cookies.setCookie(
        "calculator",
        JSON.stringify({
          postcode: this.persons[0].postcode,
          dob: this.persons[0].dob,
          deductible: this.persons[0].deductible,
          gender: this.persons[0].gender,
        }),
      );
    },
    fetchInsuranceProviders() {
      $.ajax({
        url: Craft.currentSite.baseUrl,
        method: "post",
        data: {
          action: "swissmed/calculator/insurance-providers",
        },
      }).done((data) => {
        this.insuranceProviders = data;
      });
    },
    validateStep1() {
      let errors = {};
      for (let index in this.persons) {
        let person = this.persons[index];
        for (let field of ["gender", "postcode", "dob", "deductible", "price"]) {
          if (!person[field]) {
            errors[person.uniqueId] = errors[person.uniqueId] || [];
            errors[person.uniqueId].push(field);
          }
        }
      }
      this.errors = errors;
    },
    validateStep2() {
      let errors = {};
      for (let index in this.persons) {
        let person = this.persons[index];
        for (let field of ["firstName", "lastName", "email"]) {
          if (!person[field]) {
            errors[person.uniqueId] = errors[person.uniqueId] || [];
            errors[person.uniqueId].push(field);
          }
        }
        if (person.email && !validateEmail(person.email)) {
          errors[person.uniqueId] = errors[person.uniqueId] || [];
          errors[person.uniqueId].push("email");
        }
      }
      this.errors = errors;
      this.errorConditions = !this.conditionsAccepted;
    },
  },
};
</script>
<style scoped>
.conditions {
  p {
    @apply mb-0;
  }

  a {
    @apply underline;
  }
}
</style>
